import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  PersonSimpleRun,
  PersonSimpleWalk,
  Barbell,
  Scales,
  Bicycle,
  TrendUp,
  Heartbeat,
  Lightning,
  Fire,
  ChartLineUp,
  Medal,
  User,
  House,
  CaretRight,
  CheckCircle,
  Circle,
  MapPin,
  ListBullets,
  CalendarCheck,
  X as XIcon
} from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cardio-journey-app/artifacts/tyj7q2ct_logo%20365.png";

const STREAK_MESSAGES = [
  { min: 0, max: 0, msg: "Start your streak today!" },
  { min: 1, max: 2, msg: "Great start! Keep it going!" },
  { min: 3, max: 4, msg: "You're building momentum!" },
  { min: 5, max: 6, msg: "Don't break your streak!" },
  { min: 7, max: 13, msg: "Week warrior! You're on fire!" },
  { min: 14, max: 29, msg: "Two weeks strong! Unstoppable!" },
  { min: 30, max: 89, msg: "A full month! You're transforming!" },
  { min: 90, max: 364, msg: "Incredible consistency!" },
  { min: 365, max: 9999, msg: "365 DAYS! YOU DID IT!" },
];

const MACHINES = [
  { type: 'treadmill', label: 'Treadmill', Icon: PersonSimpleRun },
  { type: 'elliptical', label: 'Elliptical', Icon: Heartbeat },
  { type: 'stair_climber', label: 'Stair Climber', Icon: TrendUp },
  { type: 'row_machine', label: 'Row Machine', Icon: Barbell },
  { type: 'running', label: 'Running', Icon: Lightning },
  { type: 'speed_walking', label: 'Speed Walking', Icon: PersonSimpleWalk },
  { type: 'stationary_bike', label: 'Stationary Bike', Icon: Bicycle },
  { type: 'weekly_weight', label: 'Weekly Weight', Icon: Scales },
];

const getStreakMessage = (streak) => {
  const entry = STREAK_MESSAGES.find(s => streak >= s.min && streak <= s.max);
  return entry ? entry.msg : "Keep going!";
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [dailyStatus, setDailyStatus] = useState(null);
  const [journeyStats, setJourneyStats] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const config = { withCredentials: true, headers };

        const [logsRes, weeklyRes, dailyRes, journeyRes] = await Promise.all([
          axios.get(`${API}/cardio-logs`, config),
          axios.get(`${API}/weekly-stats`, config),
          axios.get(`${API}/daily-status`, config),
          axios.get(`${API}/journey-stats`, config),
        ]);

        setRecentLogs(logsRes.data.slice(0, 5));
        setWeeklyStats(weeklyRes.data);
        setDailyStatus(dailyRes.data);
        setJourneyStats(journeyRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const streak = journeyStats?.current_streak || 0;
  const completionPct = journeyStats?.completion_percentage || 0;
  const currentDay = journeyStats?.current_day || 1;
  const workoutDays = journeyStats?.workout_days_completed || 0;
  const missedDays = journeyStats?.missed_days || 0;
  const daysRemaining = journeyStats?.days_remaining || 364;

  const getMachineIcon = (type) => {
    const machine = MACHINES.find(m => m.type === type);
    return machine ? machine.Icon : Lightning;
  };

  const getMachineLabel = (type) => {
    const machine = MACHINES.find(m => m.type === type);
    return machine ? machine.label : type.replace(/_/g, ' ');
  };

  return (
    <div className="min-h-screen bg-background pb-24 relative overflow-hidden" data-testid="dashboard-screen">
      {/* Watermark */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: `url(${LOGO_URL})`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '50%',
        }}
      />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div>
            <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold">Track365 Cardio</p>
            <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Welcome, {user?.name?.split(' ')[0]}
            </h1>
          </div>
          <Link to="/profile" data-testid="profile-link">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <User weight="bold" className="w-5 h-5 text-[#D4AF37]" />
              )}
            </div>
          </Link>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-5 space-y-5 relative z-10">
        {/* Streak Section */}
        <div className="premium-card p-5 text-center" data-testid="streak-section">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Fire weight="fill" className={`w-8 h-8 ${streak > 0 ? 'text-orange-500 animate-pulse' : 'text-muted-foreground'}`} />
            <span className="text-4xl font-black text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="streak-count">
              {streak}
            </span>
            <span className="text-sm text-muted-foreground mt-2">day streak</span>
          </div>
          <p className="text-sm text-[#D4AF37] font-medium" data-testid="streak-message">
            {getStreakMessage(streak)}
          </p>
          {journeyStats?.longest_streak > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              Best streak: {journeyStats.longest_streak} days
            </p>
          )}
        </div>

        {/* 365-Day Journey Countdown */}
        <div className="premium-card p-4" data-testid="journey-card">
          <div className="flex items-center gap-2 mb-3">
            <CalendarCheck weight="duotone" className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold">Your 365-Day Journey</span>
          </div>

          {/* Progress bar */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-bold text-foreground">Day {currentDay} of 365</span>
              <span className="text-sm font-bold text-[#D4AF37]">{completionPct}%</span>
            </div>
            <div className="h-3 bg-card/50 rounded-full overflow-hidden border border-border">
              <div className="h-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-1000 rounded-full" style={{ width: `${completionPct}%` }} />
            </div>
          </div>

          {/* 3-stat row: Completed | Missed | Remaining */}
          <div className="grid grid-cols-3 gap-2 mb-3" data-testid="journey-stats-row">
            <div className="text-center p-2 rounded-lg bg-green-500/10 border border-green-500/20">
              <p className="text-lg font-black text-green-500" data-testid="days-completed">{workoutDays}</p>
              <p className="text-[9px] text-green-400 uppercase tracking-wider">Completed</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-lg font-black text-red-400" data-testid="days-missed">{missedDays}</p>
              <p className="text-[9px] text-red-400 uppercase tracking-wider">Missed</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20">
              <p className="text-lg font-black text-[#D4AF37]" data-testid="days-remaining">{daysRemaining}</p>
              <p className="text-[9px] text-[#D4AF37] uppercase tracking-wider">Remaining</p>
            </div>
          </div>

          {/* Motivation text */}
          {journeyStats?.motivation && (
            <p className="text-xs text-muted-foreground text-center italic" data-testid="motivation-text">
              {journeyStats.motivation}
            </p>
          )}
        </div>

        {/* Daily Checklist */}
        {dailyStatus && (
          <div className="premium-card p-4" data-testid="daily-checklist">
            <h3 className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-3">Today's Checklist</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                {dailyStatus.workout_logged ? (
                  <CheckCircle weight="fill" className="w-5 h-5 text-green-500" />
                ) : (
                  <Circle weight="regular" className="w-5 h-5 text-muted-foreground" />
                )}
                <span className={`text-sm ${dailyStatus.workout_logged ? 'text-green-500 line-through' : 'text-foreground'}`}>
                  Log a workout
                </span>
                {dailyStatus.workout_logged && <span className="text-[10px] text-green-500 ml-auto">Done!</span>}
              </div>
              <div className="flex items-center gap-3">
                {dailyStatus.weight_logged ? (
                  <CheckCircle weight="fill" className="w-5 h-5 text-green-500" />
                ) : (
                  <Circle weight="regular" className="w-5 h-5 text-muted-foreground" />
                )}
                <span className={`text-sm ${dailyStatus.weight_logged ? 'text-green-500 line-through' : 'text-foreground'}`}>
                  Log your weight <span className="text-muted-foreground text-[10px]">(optional)</span>
                </span>
                {dailyStatus.weight_logged && <span className="text-[10px] text-green-500 ml-auto">Done!</span>}
              </div>
            </div>
          </div>
        )}

        {/* Weekly Stats Row */}
        {weeklyStats && (
          <div className="grid grid-cols-3 gap-3" data-testid="weekly-stats">
            <div className="premium-card p-3 text-center">
              <Fire weight="duotone" className="w-4 h-4 text-orange-500 mx-auto mb-1" />
              <p className="text-lg font-black text-foreground">{weeklyStats.calories}</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Cal This Week</p>
            </div>
            <div className="premium-card p-3 text-center">
              <MapPin weight="duotone" className="w-4 h-4 text-[#D4AF37] mx-auto mb-1" />
              <p className="text-lg font-black text-foreground">{weeklyStats.distance}</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Mi This Week</p>
            </div>
            <div className="premium-card p-3 text-center">
              <Lightning weight="duotone" className="w-4 h-4 text-[#D4AF37] mx-auto mb-1" />
              <p className="text-lg font-black text-foreground">{weeklyStats.sessions}</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Sessions</p>
            </div>
          </div>
        )}

        {/* What are you tracking today? */}
        <div>
          <h2 className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-3">
            What are you tracking today?
          </h2>
          <div className="grid grid-cols-4 gap-3" data-testid="machine-grid">
            {MACHINES.map(({ type, label, Icon }) => (
              <button
                key={type}
                onClick={() => navigate(`/tracker/${type}`)}
                className="premium-card p-3 flex flex-col items-center gap-2 hover:border-[#D4AF37]/40 transition-all group"
                data-testid={`machine-btn-${type}`}
              >
                <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                  <Icon weight="duotone" className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <span className="text-[10px] text-muted-foreground font-medium text-center leading-tight group-hover:text-foreground transition-colors">
                  {label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold">Recent Activity</h2>
            <Link to="/history" className="text-xs text-muted-foreground hover:text-[#D4AF37] transition-colors flex items-center gap-1" data-testid="view-history-link">
              View All <CaretRight weight="bold" className="w-3 h-3" />
            </Link>
          </div>
          {recentLogs.length > 0 ? (
            <div className="space-y-2">
              {recentLogs.map((log) => {
                const LogIcon = getMachineIcon(log.cardio_type);
                return (
                  <div key={log.log_id} className="premium-card p-3 flex items-center gap-3" data-testid={`recent-log-${log.log_id}`}>
                    <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <LogIcon weight="duotone" className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{getMachineLabel(log.cardio_type)}</p>
                      <p className="text-xs text-muted-foreground">{log.date}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-foreground">{log.duration_minutes} min</p>
                      {log.calories_burned && <p className="text-xs text-muted-foreground">{log.calories_burned} cal</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="premium-card p-6 text-center">
              <p className="text-muted-foreground text-sm">No workouts yet. Pick a machine above to start!</p>
            </div>
          )}
        </div>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-background/90 backdrop-blur-xl border-t border-border px-6 py-3 z-50">
        <div className="max-w-lg mx-auto flex items-center justify-around">
          <Link to="/dashboard" className="flex flex-col items-center gap-1 text-[#D4AF37]" data-testid="nav-home">
            <House weight="fill" className="w-6 h-6" />
            <span className="text-[10px]">Home</span>
          </Link>
          <Link to="/history" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-history">
            <ListBullets weight="duotone" className="w-6 h-6" />
            <span className="text-[10px]">History</span>
          </Link>
          <Link to="/progress" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-progress">
            <ChartLineUp weight="duotone" className="w-6 h-6" />
            <span className="text-[10px]">Progress</span>
          </Link>
          <Link to="/milestones" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-milestones">
            <Medal weight="duotone" className="w-6 h-6" />
            <span className="text-[10px]">Badges</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};

export default Dashboard;
